import type { Workspace } from '../types'
import {
    getProviderApiMode,
    type AgentConfig,
    type AgentConversationHistory,
    type Attachment,
    type HistoryContentPart,
    type HistoryMessage,
    type ResolvedRef,
    type ResponseConversationItem,
    type ResponseConversationMessageItem,
    type TranscriptEvent,
} from '../agentTypes'
import { readAttachmentAsBase64 } from './attachments'

function appendTextRefs(baseText: string, refs: ResolvedRef[]): string {
    if (refs.length === 0) return baseText
    const refBlocks = refs
        .filter((ref) => !ref.isImage)
        .map((ref) => {
            if (ref.content === null) return `\n\n<file path="${ref.path}" error="not found" />`
            const lineInfo = ref.lineStart ? `:L${ref.lineStart}${ref.lineEnd ? `-L${ref.lineEnd}` : ''}` : ''
            return `\n\n<file path="${ref.path}${lineInfo}" size="${ref.size ?? 'unknown'}">\n${ref.content}\n</file>`
        })
        .join('')
    return `${baseText}${refBlocks}`
}

function toBase64Payload(dataUrl: string): string {
    const parts = dataUrl.split(',', 2)
    return parts.length === 2 ? parts[1] : dataUrl
}

async function loadAttachmentData(
    attachment: Attachment,
    workspace: Workspace,
): Promise<string | null> {
    return readAttachmentAsBase64(attachment, workspace)
}

export async function buildChatUserContent(
    userMessage: string,
    refs: ResolvedRef[],
    attachments: Attachment[] | undefined,
    workspace: Workspace,
): Promise<string | HistoryContentPart[]> {
    const textContent = appendTextRefs(userMessage, refs)
    const parts: HistoryContentPart[] = []

    if (textContent.trim().length > 0) {
        parts.push({ type: 'text', text: textContent })
    }

    for (const ref of refs.filter((entry) => entry.isImage && entry.binaryBase64)) {
        parts.push({
            type: 'image_url',
            image_url: { url: ref.binaryBase64! },
        })
    }

    for (const attachment of attachments ?? []) {
        const dataUrl = await loadAttachmentData(attachment, workspace)
        if (!dataUrl) continue

        if (attachment.mime.startsWith('image/')) {
            parts.push({
                type: 'image_url',
                image_url: { url: dataUrl },
            })
            continue
        }

        parts.push({
            type: 'file',
            file: {
                filename: attachment.name,
                file_data: dataUrl,
            },
        })
    }

    if (parts.length === 0) return textContent
    const onlyTextPart = parts.length === 1 && parts[0]?.type === 'text'
    return onlyTextPart ? textContent : parts
}

export async function buildResponsesUserMessageItem(
    userMessage: string,
    refs: ResolvedRef[],
    attachments: Attachment[] | undefined,
    workspace: Workspace,
): Promise<ResponseConversationMessageItem> {
    const textContent = appendTextRefs(userMessage, refs)
    const content: ResponseConversationMessageItem['content'] = []

    if (textContent.trim().length > 0) {
        content.push({ type: 'input_text', text: textContent })
    }

    for (const ref of refs.filter((entry) => entry.isImage && entry.binaryBase64)) {
        content.push({
            type: 'input_image',
            image_url: ref.binaryBase64!,
            detail: 'auto',
        })
    }

    for (const attachment of attachments ?? []) {
        const dataUrl = await loadAttachmentData(attachment, workspace)
        if (!dataUrl) continue

        if (attachment.mime.startsWith('image/')) {
            content.push({
                type: 'input_image',
                image_url: dataUrl,
                detail: 'auto',
            })
            continue
        }

        content.push({
            type: 'input_file',
            file_data: toBase64Payload(dataUrl),
            filename: attachment.name,
        })
    }

    if (content.length === 0) {
        content.push({ type: 'input_text', text: userMessage })
    }

    return {
        type: 'message',
        role: 'user',
        content,
    }
}

async function buildChatConversationHistory(
    activity: TranscriptEvent[],
    workspace: Workspace,
): Promise<AgentConversationHistory> {
    const messages: HistoryMessage[] = []

    for (const event of activity) {
        switch (event.type) {
            case 'user_message':
                messages.push({
                    role: 'user',
                    content: await buildChatUserContent(event.text, event.refs, event.attachments, workspace),
                })
                break
            case 'assistant_tool_batch':
                if (event.items.length === 0) break
                messages.push({
                    role: 'assistant',
                    content: null,
                    tool_calls: event.items.map((item) => ({
                        id: item.id,
                        function: {
                            name: item.name,
                            arguments: JSON.stringify(item.args ?? {}),
                        },
                    })),
                })
                break
            case 'tool_result':
                messages.push({
                    role: 'tool',
                    content: event.result ?? '',
                    tool_call_id: event.id,
                    name: event.name,
                })
                break
            case 'assistant_message_final':
                messages.push({
                    role: 'assistant',
                    content: event.text,
                })
                break
            default:
                break
        }
    }

    return { apiMode: 'chat-completions', messages }
}

async function buildResponsesConversationHistory(
    activity: TranscriptEvent[],
    workspace: Workspace,
): Promise<AgentConversationHistory> {
    const items: ResponseConversationItem[] = []

    for (const event of activity) {
        switch (event.type) {
            case 'user_message':
                items.push(await buildResponsesUserMessageItem(event.text, event.refs, event.attachments, workspace))
                break
            case 'assistant_reasoning_item':
                if (!event.encryptedContent && (!event.summary || event.summary.length === 0)) break
                items.push({
                    type: 'reasoning',
                    id: event.id,
                    encrypted_content: event.encryptedContent,
                    summary: event.summary?.map((text) => ({ type: 'summary_text', text })),
                    status: event.status ?? 'completed',
                })
                break
            case 'assistant_tool_batch':
                for (const item of event.items) {
                    items.push({
                        type: 'function_call',
                        id: item.responseId ?? `fc_${item.id}`,
                        call_id: item.id,
                        name: item.name,
                        arguments: JSON.stringify(item.args ?? {}),
                        status: item.status ?? 'completed',
                    })
                }
                break
            case 'tool_result':
                items.push({
                    type: 'function_call_output',
                    id: `fco_${event.id}`,
                    call_id: event.id,
                    output: event.result ?? '',
                })
                break
            case 'assistant_message_final':
                items.push({
                    type: 'message',
                    id: event.id ?? `msg_${event.timestamp}`,
                    role: 'assistant',
                    status: event.status ?? 'completed',
                    ...(event.phase ? { phase: event.phase } : {}),
                    content: event.content ?? [
                        {
                            type: 'output_text',
                            text: event.text,
                            annotations: [],
                        },
                    ],
                })
                break
            default:
                break
        }
    }

    return { apiMode: 'responses', items }
}

export async function buildConversationHistoryFromActivity(
    config: AgentConfig,
    activity: TranscriptEvent[],
    workspace: Workspace,
): Promise<AgentConversationHistory> {
    if (getProviderApiMode(config.provider) === 'responses') {
        return buildResponsesConversationHistory(activity, workspace)
    }
    return buildChatConversationHistory(activity, workspace)
}
